import { useState, useCallback } from 'react';
import { CircularProgress } from '../CircularProgress';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ComposedChart, Line,
} from 'recharts';
import { KPICell, SectionFrame, performanceColor, type PeriodContentProps } from './shared';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import { useUserPermissions } from '../../../contexts/UserPermissionsContext';
import { usePreferences, useFormatters } from '../../../contexts/PreferencesContext';
import {
  YTDDrilldownModal,
  type YTDDrilldownMode,
  type DashboardFilterScope,
} from './YTDDrilldownModal';

const PALETTE = {
  cumSpend: '#0f172a',     // slate-900 — cumulative line stays dark
  monthSpend: '#3b82f6',   // blue-500 — punchier month bars
  arrears: '#dc2626',
  target: '#94a3b8',
  ncr: '#dc2626',          // align with quality-red discipline
};

interface DrilldownState {
  mode: YTDDrilldownMode;
  periodStart: string;
  periodEnd: string;
  periodLabel?: string;
}

/** Tile value wrapped in a clickable region without altering layout/typography. */
function ClickableValue({
  onClick, children, title,
}: { onClick: () => void; children: React.ReactNode; title: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="bg-transparent border-0 p-0 m-0 cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary/40 rounded"
    >
      {children}
    </button>
  );
}

export function YTD({ data, formatCurrency, filters }: PeriodContentProps) {
  const ytd = data.ytd;
  const qualityLabel = useQualityMeasureLabel();
  // Hidden for users whose sites have the Quality module off — see CurrentWeek.
  const { hasModuleAccess } = useUserPermissions();
  const showQuality = hasModuleAccess('quality');
  // OTIF/KPI percentage decimals from Admin Setup -> Regional -> Decimal
  // Precision (customer review 2026-07). 0 keeps the historical whole-number
  // ring labels; 1-2 shows e.g. 96.2%.
  const { preferences } = usePreferences();
  const { formatCompactCurrency } = useFormatters();
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0;

  // Mirror the gating used by CurrentWeek / DefaultTab / SiteKPITab so the
  // customer's PerformanceSettings.delivery_metric_mode controls whether
  // OTIF, OTD, or both blocks appear here too.
  // CR-6543 r2: tenant-configured targets (Admin Setup) — was hardcoded 95.
  const otifTarget = data.otif_target ?? 95;
  const otdTarget = data.otd_target ?? 95;
  const deliveryMode = data.delivery_metric_mode ?? 'both';
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only';
  const showOTD  = deliveryMode === 'both' || deliveryMode === 'otd_only';

  // ``filters`` is declared optional on PeriodContentProps for legacy
  // sub-components that don't yet wire drilldowns; the YTD shell always
  // threads it through, so we coerce to a default scope when absent
  // rather than guarding every drilldown call site.
  const effectiveFilters: DashboardFilterScope = filters ?? { erp_order_type: '4' };

  // YTD period: Jan 1 of current year → today. Backend exposes Jan 1 via
  // the first per-month bucket's month_start (no separate field) so the
  // drilldown stays in lockstep with whatever year the dashboard
  // computed against.
  const ytdStart = data.per_month.months[0]?.month_start ?? data.today;
  const ytdEnd = data.today;

  const [drilldown, setDrilldown] = useState<DrilldownState | null>(null);

  const openYTD = useCallback((mode: YTDDrilldownMode) => {
    setDrilldown({ mode, periodStart: ytdStart, periodEnd: ytdEnd });
  }, [ytdStart, ytdEnd]);

  const openMonth = useCallback((mode: YTDDrilldownMode, monthIdx: number) => {
    const m = data.per_month.months[monthIdx];
    if (!m) return;
    setDrilldown({
      mode,
      periodStart: m.month_start,
      periodEnd: m.month_end,
      periodLabel: m.label,
    });
  }, [data.per_month.months]);

  // Real per-month YTD data from the backend — sums to exactly the YTD
  // top-tile total. ``otif`` / ``otd`` are null when no GRN had an
  // attributed status that month, so the line breaks instead of dropping
  // to a misleading 0%.
  let cum = 0;
  const monthly = data.per_month.months.map((m, idx) => {
    cum += m.cost.received_value;
    const hasDeliveryData = m.delivery.total_due > 0;
    return {
      idx,
      month: m.label,
      received: m.cost.received_value,
      open:     m.cost.open_value,
      committed: m.cost.total_value,
      cumReceived: cum,
      ncrs: m.quality.ncrs,
      otif: hasDeliveryData ? m.delivery.otif_pct : null,
      otd:  hasDeliveryData ? m.delivery.otd_pct  : null,
      // Chart draws one shared dashed target — OTIF's unless the
      // tenant runs OTD-only.
      target: deliveryMode === 'otd_only' ? otdTarget : otifTarget,
    };
  });

  // Recharts onClick for <Bar>/<Line> hands us either a bar's data entry
  // (which has the row's keys flattened in `payload`) or a line's active
  // point. Both expose `idx` either at the top level or under
  // `payload.idx` — pull it from whichever is set.
  type RechartsClickPayload = { payload?: { idx?: number }; idx?: number };
  const handleBarClick = (mode: YTDDrilldownMode) => (payload: unknown) => {
    const p = payload as RechartsClickPayload;
    const idx = p?.payload?.idx ?? p?.idx;
    if (typeof idx === 'number') openMonth(mode, idx);
  };

  return (
    <div className="space-y-6">
      <div className={`grid gap-4 ${showQuality ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {showQuality && (
          <SectionFrame title="Quality" accent="red" subtitle="Jan 1 → today">
            <div className="grid grid-cols-2 gap-3">
              <KPICell
                label={qualityLabel}
                value={
                  <ClickableValue onClick={() => openYTD('quality_ppm')} title="Drill down: NCRs with reject_qty (YTD)">
                    <span className="text-2xl font-semibold tabular-nums">{ytd.quality.ppm.toLocaleString()}</span>
                  </ClickableValue>
                }
                foot={`${ytd.quality.reject_qty.toLocaleString()} rej`}
              />
              <KPICell
                label="NCRs"
                value={
                  <ClickableValue onClick={() => openYTD('quality_ncrs')} title="Drill down: all NCRs raised YTD">
                    <span className="text-2xl font-semibold tabular-nums">{ytd.quality.ncrs.toLocaleString()}</span>
                  </ClickableValue>
                }
                foot="cumulative"
              />
            </div>
          </SectionFrame>
        )}

        <SectionFrame title="Delivery" accent="green" subtitle="Jan 1 → today">
          <div
            className={`grid gap-3 items-start ${
              showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'
            }`}
          >
            {showOTIF && (
              <KPICell
                label="OTIF"
                value={
                  <ClickableValue onClick={() => openYTD('delivery_otif')} title="Drill down: YTD schedules (OTIF view)">
                    <CircularProgress percentage={ytd.delivery.otif_pct} size={80} strokeWidth={6} color={performanceColor(ytd.delivery.otif_pct, otifTarget)} decimals={kpiDecimals} />
                  </ClickableValue>
                }
                foot={`${ytd.delivery.otif_met.toLocaleString()}/${ytd.delivery.total_due.toLocaleString()} schedules`}
              />
            )}
            {showOTD && (
              <KPICell
                label="OTD"
                value={
                  <ClickableValue onClick={() => openYTD('delivery_otd')} title="Drill down: YTD schedules (OTD view)">
                    <CircularProgress percentage={ytd.delivery.otd_pct} size={80} strokeWidth={6} color={performanceColor(ytd.delivery.otd_pct, otdTarget)} decimals={kpiDecimals} />
                  </ClickableValue>
                }
                foot={`${ytd.delivery.otd_met.toLocaleString()}/${(ytd.delivery.otd_due ?? ytd.delivery.total_due).toLocaleString()} within firm`}
              />
            )}
            <KPICell
              label="Appeals"
              value={
                <ClickableValue onClick={() => openYTD('delivery_appeals')} title="Drill down: appealed schedules YTD">
                  <span className="text-2xl font-semibold tabular-nums">{ytd.delivery.appeals}</span>
                </ClickableValue>
              }
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle="schedules due YTD">
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label="Open Schedule Lines"
              value={
                <ClickableValue onClick={() => openYTD('cost_open')} title="Drill down: open-balance schedules YTD">
                  <span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.open_value)}</span>
                </ClickableValue>
              }
              foot={`${ytd.cost.open_lines.toLocaleString()} of ${ytd.cost.total_lines.toLocaleString()} lines`}
            />
            <KPICell
              label="Received (YTD)"
              value={
                <ClickableValue onClick={() => openYTD('cost_received')} title="Drill down: received schedules YTD">
                  <span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.received_value)}</span>
                </ClickableValue>
              }
              foot={`of ${formatCurrency(ytd.cost.total_value)} committed`}
            />
            <KPICell
              label="Arrears"
              value={
                <ClickableValue onClick={() => openYTD('cost_arrears')} title="Drill down: past-due open schedules">
                  <span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.arrears_value)}</span>
                </ClickableValue>
              }
              foot={`${ytd.cost.arrears_lines.toLocaleString()} past-due lines`}
            />
          </div>
        </SectionFrame>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">Schedule Value by Month — Received vs Open</CardTitle>
          <p className="text-[11px] text-muted-foreground">Bars: schedule value due that month (split received / still open) • Line: YTD cumulative received • Click a bar to drill in</p>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthly} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatCompactCurrency(v)} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatCompactCurrency(v)} />
                <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => formatCurrency(v)} />
                <Bar
                  yAxisId="left" dataKey="received" stackId="cost" fill={PALETTE.monthSpend} name="Received" radius={[0, 0, 0, 0]}
                  onClick={handleBarClick('cost_received')}
                  style={{ cursor: 'pointer' }}
                  activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }}
                />
                <Bar
                  yAxisId="left" dataKey="open"     stackId="cost" fill="#cbd5e1"             name="Open"     radius={[3, 3, 0, 0]}
                  onClick={handleBarClick('cost_open')}
                  style={{ cursor: 'pointer' }}
                  activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }}
                />
                <Line yAxisId="right" type="monotone" dataKey="cumReceived" stroke={PALETTE.cumSpend} strokeWidth={2.5} dot={{ r: 3 }} name="Cumulative received" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className={`grid gap-4 ${(showOTIF || showOTD) ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {(showOTIF || showOTD) && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">
                Delivery — {showOTIF && showOTD ? 'OTIF / OTD' : showOTIF ? 'OTIF' : 'OTD'} by Month
              </CardTitle>
              <p className="text-[11px] text-muted-foreground">Target {monthly[0]?.target ?? 95}% (dashed) • Click a point to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthly} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} unit="%" />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                    <ReferenceLine y={monthly[0]?.target ?? 95} stroke={PALETTE.target} strokeDasharray="4 4" />
                    {showOTIF && (
                      <Line
                        type="monotone" dataKey="otif" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }}
                        name="OTIF" connectNulls={false}
                        activeDot={{
                          r: 5,
                          onClick: (_e: unknown, p: unknown) => handleBarClick('delivery_otif')(p),
                          style: { cursor: 'pointer' },
                        }}
                      />
                    )}
                    {showOTD && (
                      <Line
                        type="monotone" dataKey="otd"  stroke="#0284c7" strokeWidth={2.5} dot={{ r: 3 }}
                        name="OTD"  connectNulls={false}
                        activeDot={{
                          r: 5,
                          onClick: (_e: unknown, p: unknown) => handleBarClick('delivery_otd')(p),
                          style: { cursor: 'pointer' },
                        }}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">NCRs — Monthly</CardTitle>
            <p className="text-[11px] text-muted-foreground">Issues raised against suppliers • Click a bar to drill in</p>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Bar
                    dataKey="ncrs" fill={PALETTE.ncr} name="NCRs" radius={[3, 3, 0, 0]}
                    onClick={handleBarClick('quality_ncrs')}
                    style={{ cursor: 'pointer' }}
                    activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {drilldown && (
        <YTDDrilldownModal
          open={!!drilldown}
          onClose={() => setDrilldown(null)}
          mode={drilldown.mode}
          filters={effectiveFilters}
          periodStart={drilldown.periodStart}
          periodEnd={drilldown.periodEnd}
          todayIso={data.today}
          periodLabel={drilldown.periodLabel}
          arrearsBufferDays={data.arrears_buffer_days ?? 0}
        />
      )}
    </div>
  );
}
